import React, {
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";
import axios from "axios";
import {
    Alert
} from "react-bootstrap";
import PropTypes from "prop-types";

import ShopsContext from "../../../context/ShopsContext";
import Loading from "../../../components/Loading";
import CategoryForm from "../components/CategoryForm";
import handleBackofficeSessionExpired from "../../../utilities/handleBackofficeSessionExpired";
import {
    getCategorySlug,
    getCategoryTitle
} from "../../../utilities/categoryFields";
import normalizeCategorySlug from "../../../utilities/normalizeCategorySlug";
import getCategorySaveErrorMessage from "../functions/getCategorySaveErrorMessage";

function CategoryDetailEdit({ category, setCategory }) {
    const shopsContext = useContext(ShopsContext);
    const [formState, setFormState] = useState(null);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if(category) {
            setFormState({
                title: getCategoryTitle(category),
                slug: getCategorySlug(category),
                description: category.description ?? ""
            });
        }
    }, [category]);

    const onChange = useCallback((newState) => {
        setFormState((prevState) => ({
            ...prevState,
            ...newState
        }));
        setSuccess(false);
    }, []);

    const onSave = useCallback(async () => {
        if(formState.title.trim().length === 0) {
            setError("Titel is vereist.");
            window.scroll({ top: 0, behavior: "smooth" });
            return;
        }
        if(normalizeCategorySlug(formState.slug).length === 0) {
            setError("Slug is vereist.");
            window.scroll({ top: 0, behavior: "smooth" });
            return;
        }

        setError(null);
        setSaving(true);
        setSuccess(false);

        try {
            const response = await axios.post("/setCategory", {
                shopId: shopsContext.currentShop.id,
                categoryId: category.id,
                title: formState.title.trim(),
                description: formState.description,
                slug: formState.slug
            });

            if(response.data.valid) {
                setFormState({
                    title: getCategoryTitle(response.data.category),
                    slug: getCategorySlug(response.data.category),
                    description: response.data.category.description ?? ""
                });
                setCategory(response.data.category);
                setSuccess(true);
                return;
            }
            if(handleBackofficeSessionExpired(response.data)) {
                return;
            }
            setError(getCategorySaveErrorMessage(response.data.error));
        } catch(requestError) {
            if(handleBackofficeSessionExpired(requestError.response?.data)) {
                return;
            }
            console.error(requestError);
            setError("Er ging iets fout. Probeer het later opnieuw.");
        } finally {
            window.scroll({ top: 0, behavior: "smooth" });
            setSaving(false);
        }
    }, [category, formState, setCategory, shopsContext.currentShop.id]);

    if(formState === null) {
        return (
            <Loading/>
        );
    }

    return (
        <React.Fragment>
            { error ? (
                <Alert variant="danger">{ error }</Alert>
            ) : success && (
                <Alert variant="success">
                    Wijzigingen opgeslagen!
                </Alert>
            )}
            <CategoryForm
                state={ formState }
                onChange={ onChange }
                loading={ saving }
                onSave={ onSave }
                previewCategoryId={ category.id }
            />
        </React.Fragment>
    );
}
CategoryDetailEdit.propTypes = {
    category: PropTypes.object,
    setCategory: PropTypes.func.isRequired
}

export default React.memo(CategoryDetailEdit);
